import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import dayjs from "dayjs";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import LeftMenu from "../../components/left-menu";

import {
  Grid,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  TextField,
  MenuItem,
  List,
  ListItemButton,
  ListItemText,
  Chip,
} from "@mui/material";

const LEGAL_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/legal`;

const RESPONSIBILITY_OPTIONS = [
  "Internal Legal",
  "CEO/Reinner",
  "Sales Person",
  "Admin (Bekasi)",
  "Admin (Serpong)",
  "Admin (DM)",
  "Admin (Kokas)",
  "Sistem Pemerintah",
  "Sistem OSS",
  "Notaris",
  "Klien",
];

function normalizeText(value = "") {
  return String(value || "").trim();
}

function toDateOrNull(value) {
  if (!value) return null;

  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value.toDate() : null;
  }

  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toDayjsOrNull(value) {
  if (!value) return null;

  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value : null;
  }

  const d = dayjs(value);
  return d.isValid() ? d : null;
}

function formatDisplayDate(value) {
  const d = toDayjsOrNull(value);
  if (!d) return "-";
  return d.format("DD/MM/YY");
}

function formatDisplayDateLong(value) {
  const d = toDayjsOrNull(value);
  if (!d) return "-";
  return d.format("DD/MM/YYYY");
}

function formatHistoryDateTime(value) {
  const d = toDayjsOrNull(value);
  if (!d) return "-";
  return d.format("DD/MM/YYYY HH:mm");
}

function formatInputDate(value) {
  const d = toDayjsOrNull(value);
  if (!d) return "";
  return d.format("YYYY-MM-DD");
}

function todayInputValue() {
  return dayjs().format("YYYY-MM-DD");
}

function buildTitle(serviceLabel = "", entityName = "") {
  const service = normalizeText(serviceLabel);
  const entity = normalizeText(entityName);

  if (!service && !entity) return "";
  if (!entity) return service;
  if (!service) return entity;

  return `${service} ${entity}`;
}

function getTemplateStages(template) {
  return Array.isArray(template?.stages) ? template.stages : [];
}

function getStageLabel(stage) {
  return normalizeText(stage?.label || stage?.name);
}

function findTemplateByKey(templates = [], key = "") {
  return (
    (Array.isArray(templates) ? templates : []).find((item) => item?.key === key) ||
    null
  );
}

function findStageByKey(template, stageKey = "") {
  return getTemplateStages(template).find((item) => item?.key === stageKey) || null;
}

function uniqueEmployeeNames(employees = [], actorName = "") {
  const set = new Set();

  (Array.isArray(employees) ? employees : []).forEach((item) => {
    const value = normalizeText(item?.full_name || item?.name || item?.username);
    if (value) set.add(value);
  });

  if (normalizeText(actorName)) {
    set.add(normalizeText(actorName));
  }

  return Array.from(set);
}

function getSuggestedResponsibility(stage) {
  const direct = normalizeText(stage?.defaultResponsibility);
  if (direct) return direct;

  const label = normalizeText(getStageLabel(stage)).toLowerCase();

  if (
    label.includes("pengumpulan data") ||
    label.includes("tanda tangan") ||
    label.includes("rekening")
  ) {
    return "Klien";
  }

  if (
    label.includes("akte") ||
    label.includes("minuta") ||
    label.includes("sk")
  ) {
    return "Notaris";
  }

  if (
    label.includes("oss") ||
    label.includes("nib") ||
    label.includes("komitmen") ||
    label.includes("izin")
  ) {
    return "Sistem OSS";
  }

  if (
    label.includes("npwp") ||
    label.includes("pkp") ||
    label.includes("kpp") ||
    label.includes("djp") ||
    label.includes("verifikasi")
  ) {
    return "Sistem Pemerintah";
  }

  return "Internal Legal";
}

function toInquiryFromProcess(processItem) {
  if (!processItem) return null;

  const inquiryId = processItem?.inquiryId?._id || processItem?.inquiryId || "";

  const inquiry = {
    _id: inquiryId,
    Nama: processItem?.clientName || "",
    Phone: processItem?.inquiryPhone || "",
    Sales: processItem?.salesName || "",
  };

  if (!inquiry._id && !inquiry.Nama && !inquiry.Phone && !inquiry.Sales) {
    return null;
  }

  return inquiry;
}

function buildInitialForm({ processItem, templates, actorName }) {
  const activeTemplates = (Array.isArray(templates) ? templates : []).filter(
    (item) => item?.isActive !== false
  );

  const selectedTemplate =
    findTemplateByKey(templates, processItem?.serviceKey) ||
    activeTemplates[0] ||
    templates[0] ||
    null;

  const stages = getTemplateStages(selectedTemplate);
  const firstStage = stages[0] || null;

  const currentStageKey =
    normalizeText(processItem?.currentStageKey) || normalizeText(firstStage?.key);

  const currentStage =
    findStageByKey(selectedTemplate, currentStageKey) || firstStage || null;

  return {
    inquiryId: normalizeText(processItem?.inquiryId?._id || processItem?.inquiryId),
    entityName: normalizeText(processItem?.entityName),
    title:
      normalizeText(processItem?.title) ||
      buildTitle(selectedTemplate?.name || "", processItem?.entityName || ""),
    serviceKey: normalizeText(selectedTemplate?.key),
    currentStageKey: normalizeText(currentStage?.key),
    currentResponsibility:
      normalizeText(processItem?.currentResponsibility) ||
      getSuggestedResponsibility(currentStage),
    currentPic:
      normalizeText(processItem?.currentPic) ||
      normalizeText(processItem?.salesName),
    assignedToName:
      normalizeText(processItem?.assignedToName) || normalizeText(actorName),
    startDate: formatInputDate(processItem?.startDate) || todayInputValue(),
    nextDueDate: formatInputDate(processItem?.nextDueDate),
    nextActionNote: normalizeText(processItem?.nextActionNote),
  };
}

function extractListFromResponse(resp, keys = ["items", "data"]) {
  const data = resp?.data || {};

  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }

  return [];
}

function extractObjectFromResponse(resp, keys = ["item", "data"]) {
  const data = resp?.data || {};

  for (const key of keys) {
    if (data?.[key] && !Array.isArray(data?.[key]) && typeof data?.[key] === "object") {
      return data[key];
    }
  }

  return null;
}

function extractErrorMessage(error, fallback = "Terjadi kesalahan.") {
  return error?.response?.data?.message || error?.message || fallback;
}

function getAxiosErrorMeta(error) {
  return {
    message: error?.message,
    status: error?.response?.status,
    method: error?.config?.method,
    url: error?.config?.url,
    params: error?.config?.params,
    data: error?.response?.data,
  };
}

function logAxiosError(label, error) {
  console.error(label, getAxiosErrorMeta(error), error);
}

function isOwnerLike(actorName = "", actorRole = "", username = "") {
  const role = normalizeText(actorRole).toLowerCase();
  const user = normalizeText(username).toLowerCase();
  const name = normalizeText(actorName).toLowerCase();

  return (
    role === "ceo" ||
    role === "owner" ||
    role === "super admin" ||
    role === "superadmin" ||
    user === "reinner" ||
    name === "reinner"
  );
}

function splitProcessesForBoardLocal(processes = []) {
  const today = formatInputDate(new Date());

  const source = Array.isArray(processes) ? processes : [];
  const activeItems = source.filter((item) => !item?.isCompleted);
  const completedItems = source.filter((item) => item?.isCompleted);

  const urgent = [];
  const pending = [];

  activeItems.forEach((item) => {
    const due = formatInputDate(item?.nextDueDate);

    if (due && due <= today) {
      urgent.push(item);
    } else {
      pending.push(item);
    }
  });

  const activeSorter = (a, b) => {
    const aDue = toDateOrNull(a?.nextDueDate);
    const bDue = toDateOrNull(b?.nextDueDate);

    if (aDue && bDue && aDue.getTime() !== bDue.getTime()) {
      return aDue.getTime() - bDue.getTime();
    }

    if (aDue && !bDue) return -1;
    if (!aDue && bDue) return 1;

    const aStart = toDateOrNull(a?.startDate);
    const bStart = toDateOrNull(b?.startDate);

    if (aStart && bStart && aStart.getTime() !== bStart.getTime()) {
      return aStart.getTime() - bStart.getTime();
    }

    return normalizeText(a?.title).localeCompare(normalizeText(b?.title), "id");
  };

  const completedSorter = (a, b) => {
    const aCompleted = toDateOrNull(a?.completedAt);
    const bCompleted = toDateOrNull(b?.completedAt);

    if (aCompleted && bCompleted && aCompleted.getTime() !== bCompleted.getTime()) {
      return bCompleted.getTime() - aCompleted.getTime();
    }

    if (aCompleted && !bCompleted) return -1;
    if (!aCompleted && bCompleted) return 1;

    const aUpdated = toDateOrNull(a?.updatedAt);
    const bUpdated = toDateOrNull(b?.updatedAt);

    if (aUpdated && bUpdated && aUpdated.getTime() !== bUpdated.getTime()) {
      return bUpdated.getTime() - aUpdated.getTime();
    }

    return normalizeText(a?.title).localeCompare(normalizeText(b?.title), "id");
  };

  return {
    urgent: [...urgent].sort(activeSorter),
    pending: [...pending].sort(activeSorter),
    completed: [...completedItems].sort(completedSorter),
  };
}

function normalizeStageText(item = {}) {
  return `${normalizeText(item?.currentStageKey)} ${normalizeText(item?.currentStageLabel)}`
    .toLowerCase()
    .trim();
}

function isDoneStageText(value = "") {
  const txt = normalizeText(value).toLowerCase();
  return (
    txt.includes("done") ||
    txt.includes("selesai") ||
    txt.includes("completed")
  );
}

function isCancelledStageText(value = "") {
  const txt = normalizeText(value).toLowerCase();
  return (
    txt.includes("cancel") ||
    txt.includes("cancelled") ||
    txt.includes("batal")
  );
}

function getProcessVisualStatus(item = {}) {
  const stageText = normalizeStageText(item);
  const due = toDayjsOrNull(item?.nextDueDate);
  const today = dayjs().startOf("day");

  if (isCancelledStageText(stageText)) {
    return {
      key: "cancelled",
      label: "Cancelled",
      color: "#7a7a7a",
      bg: "#f1f1f1",
      text: "#4f4f4f",
    };
  }

  if (isDoneStageText(stageText) || item?.isCompleted === true) {
    return {
      key: "done",
      label: "Done",
      color: "#2e7d32",
      bg: "#e8f5e9",
      text: "#1b5e20",
    };
  }

  if (due && due.startOf("day").isBefore(today)) {
    return {
      key: "overdue",
      label: "Overdue",
      color: "#d32f2f",
      bg: "#ffebee",
      text: "#b71c1c",
    };
  }

  if (due && due.startOf("day").isSame(today)) {
    return {
      key: "due-today",
      label: "Due Today",
      color: "#ef6c00",
      bg: "#fff3e0",
      text: "#e65100",
    };
  }

  return {
    key: "active",
    label: "Active",
    color: "#1565c0",
    bg: "#e3f2fd",
    text: "#0d47a1",
  };
}

function formatRelativeAge(value) {
  const d = toDayjsOrNull(value);
  if (!d) return "-";

  const now = dayjs();
  const dayDiff = now.startOf("day").diff(d.startOf("day"), "day");

  if (dayDiff <= 0) return "Hari ini";
  if (dayDiff === 1) return "1 hari lalu";
  return `${dayDiff} hari lalu`;
}

function getProcessDurationDays(item = {}) {
  const start = toDayjsOrNull(item?.startDate);
  const end = toDayjsOrNull(item?.completedAt || item?.updatedAt);

  if (!start || !end) return null;

  const diff = end.startOf("day").diff(start.startOf("day"), "day");
  return diff < 0 ? 0 : diff;
}

function formatProcessDurationText(item = {}) {
  const days = getProcessDurationDays(item);

  if (days === null) return "-";
  if (days === 0) return "0 hari";
  if (days === 1) return "1 hari";
  return `${days} hari`;
}

function getHumanFieldLabel(field = "") {
  const map = {
    entityName: "Nama Entitas",
    title: "Judul Process",
    currentStageKey: "Stage",
    currentStageLabel: "Stage",
    currentResponsibility: "Tanggung Jawab Saat Ini",
    currentPic: "PIC Saat Ini",
    assignedToName: "Assigned To",
    startDate: "Tanggal Mulai",
    nextDueDate: "Due Date Selanjutnya",
    nextActionNote: "Next To Do / Catatan",
    serviceKey: "Layanan",
    isCompleted: "Status Selesai",
  };

  return map[field] || field || "Perubahan";
}

function formatChangeValue(field = "", value = "") {
  if (value === null || value === undefined || value === "") return "-";

  if (field === "startDate" || field === "nextDueDate") {
    return formatDisplayDateLong(value);
  }

  if (field === "isCompleted") {
    if (String(value).toLowerCase() === "true") return "Ya";
    if (String(value).toLowerCase() === "false") return "Belum";
  }

  if (field === "serviceKey") {
    const map = {
      "pendaftaran-merek": "Pendaftaran Merek",
      "pendirian-pt": "Pendirian PT",
      "pendirian-cv": "Pendirian CV",
    };
    return map[value] || String(value);
  }

  return String(value);
}

function describeHistoryEntry(entry = {}) {
  const changes = Array.isArray(entry?.changes) ? entry.changes : [];
  const mode = normalizeText(entry?.mode).toLowerCase();

  if (mode === "create") {
    return entry?.summary || "Legal process dibuat";
  }

  const fieldNames = changes.map((c) => normalizeText(c?.field)).filter(Boolean);

  if (fieldNames.includes("currentStageKey") || fieldNames.includes("currentStageLabel")) {
    const stageChange =
      changes.find((c) => normalizeText(c?.field) === "currentStageLabel") ||
      changes.find((c) => normalizeText(c?.field) === "currentStageKey");

    const nextStage = formatChangeValue(stageChange?.field, stageChange?.to);
    return nextStage !== "-" ? `Stage dipindahkan ke ${nextStage}` : "Stage diperbarui";
  }

  if (fieldNames.includes("nextDueDate")) {
    return "Due date diperbarui";
  }

  if (fieldNames.includes("assignedToName")) {
    return "Assigned to diperbarui";
  }

  if (fieldNames.includes("nextActionNote")) {
    return "Next to do diperbarui";
  }

  if (fieldNames.includes("currentResponsibility")) {
    return "Tanggung jawab diperbarui";
  }

  if (fieldNames.includes("currentPic")) {
    return "PIC saat ini diperbarui";
  }

  return entry?.summary || "Legal process diupdate";
}

function getHistoryAccent(entry = {}) {
  const changes = Array.isArray(entry?.changes) ? entry.changes : [];
  const mode = normalizeText(entry?.mode).toLowerCase();
  const summary = normalizeText(entry?.summary).toLowerCase();

  if (mode === "create") {
    return {
      color: "#7b1fa2",
      bg: "#f3e5f5",
      text: "#4a148c",
      chip: "Create",
    };
  }

  const stageKeyChange = changes.find(
    (c) => normalizeText(c?.field).toLowerCase() === "currentstagekey"
  );
  const stageLabelChange = changes.find(
    (c) => normalizeText(c?.field).toLowerCase() === "currentstagelabel"
  );

  const nextStageText = `${formatChangeValue(stageKeyChange?.field, stageKeyChange?.to)} ${formatChangeValue(
    stageLabelChange?.field,
    stageLabelChange?.to
  )}`.toLowerCase();

  if (isCancelledStageText(nextStageText) || summary.includes("cancel") || summary.includes("batal")) {
    return {
      color: "#7a7a7a",
      bg: "#f1f1f1",
      text: "#4f4f4f",
      chip: "Update",
    };
  }

  if (isDoneStageText(nextStageText)) {
    return {
      color: "#2e7d32",
      bg: "#e8f5e9",
      text: "#1b5e20",
      chip: "Update",
    };
  }

  if (stageKeyChange || stageLabelChange) {
    return {
      color: "#1565c0",
      bg: "#e3f2fd",
      text: "#0d47a1",
      chip: "Update",
    };
  }

  const hasDueDateChange = changes.some(
    (c) => normalizeText(c?.field).toLowerCase() === "nextduedate"
  );

  if (hasDueDateChange) {
    return {
      color: "#ef6c00",
      bg: "#fff3e0",
      text: "#e65100",
      chip: "Update",
    };
  }

  return {
    color: "#5f6368",
    bg: "#f5f5f5",
    text: "#424242",
    chip: "Update",
  };
}

function SummaryInfo({ label, value }) {
  return (
    <Box
      sx={{
        p: 1.4,
        borderRadius: 1.5,
        backgroundColor: "#fff",
        border: "1px solid rgba(0,0,0,0.08)",
      }}
    >
      <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.45 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.4 }}>
        {value || "-"}
      </Typography>
    </Box>
  );
}

function HistoryCard({ entry, isLast }) {
  const accent = getHistoryAccent(entry);
  const changes = Array.isArray(entry?.changes) ? entry.changes : [];

  return (
    <Box sx={{ position: "relative", pl: 3.3 }}>
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 10,
          width: 14,
          height: 14,
          borderRadius: "50%",
          backgroundColor: accent.color,
          border: "3px solid #fff",
          boxShadow: "0 0 0 2px rgba(0,0,0,0.08)",
          zIndex: 2,
        }}
      />
      {!isLast && (
        <Box
          sx={{
            position: "absolute",
            left: 6.5,
            top: 26,
            bottom: -24,
            width: 2,
            backgroundColor: "rgba(0,0,0,0.08)",
          }}
        />
      )}

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          p: 2,
          backgroundColor: "#fff",
          borderLeft: `8px solid ${accent.color}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
              {describeHistoryEntry(entry)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
              {formatHistoryDateTime(entry?.changedAt)}
              {entry?.changedBy ? ` • ${entry.changedBy}` : ""}
            </Typography>
          </Box>

          <Chip
            label={accent.chip}
            size="small"
            sx={{
              backgroundColor: accent.bg,
              color: accent.text,
              fontWeight: 700,
            }}
          />
        </Box>

        {!!normalizeText(entry?.note) && (
          <Box
            sx={{
              mt: 1.4,
              p: 1.25,
              borderRadius: 1.5,
              backgroundColor: "rgba(0,0,0,0.03)",
            }}
          >
            <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 0.35 }}>
              Catatan
            </Typography>
            <Typography sx={{ fontSize: 15, whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
              {entry.note}
            </Typography>
          </Box>
        )}

        {changes.length > 0 && (
          <Box sx={{ mt: 1.4, display: "flex", flexDirection: "column", gap: 1 }}>
            {changes.map((change, idx) => (
              <Box
                key={`${change?.field || "field"}-${idx}`}
                sx={{
                  p: 1.2,
                  borderRadius: 1.5,
                  backgroundColor: "rgba(0,0,0,0.025)",
                }}
              >
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                  {getHumanFieldLabel(change?.field)}
                </Typography>
                <Typography sx={{ fontSize: 15, mt: 0.2, lineHeight: 1.5 }}>
                  <strong>{formatChangeValue(change?.field, change?.from)}</strong>
                  {"  "}→{"  "}
                  <strong>{formatChangeValue(change?.field, change?.to)}</strong>
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
}

function BoardRow({ item, onRowClick }) {
  const visual = getProcessVisualStatus(item);

  return (
    <Box
      onClick={() => onRowClick(item)}
      sx={{
        display: "grid",
        gridTemplateColumns: "8px 2fr 0.95fr 1.1fr 0.9fr 0.8fr",
        gap: 2,
        alignItems: "stretch",
        borderBottom: "1px solid rgba(0,0,0,0.12)",
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "rgba(21, 101, 192, 0.05)",
        },
      }}
    >
      <Box sx={{ backgroundColor: visual.color, borderRadius: "0 4px 4px 0" }} />

      <Box sx={{ py: 1.4, minWidth: 0 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.3 }}>
          {item?.title || "-"}
        </Typography>
        <Typography
          sx={{
            mt: 0.4,
            fontSize: 14,
            color: "text.secondary",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item?.clientName || "-"} • {item?.assignedToName || "-"}
        </Typography>
      </Box>

      <Box sx={{ py: 1.4 }}>
        <Chip
          label={visual.label}
          size="small"
          sx={{
            backgroundColor: visual.bg,
            color: visual.text,
            fontWeight: 700,
          }}
        />
      </Box>

      <Box sx={{ py: 1.4, minWidth: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
          {item?.currentStageLabel || item?.currentStageKey || "-"}
        </Typography>
        <Typography sx={{ mt: 0.35, fontSize: 13, color: "text.secondary" }}>
          {item?.currentResponsibility || "-"}
        </Typography>
      </Box>

      <Box sx={{ py: 1.4 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
          {formatDisplayDate(item?.nextDueDate)}
        </Typography>
        <Typography sx={{ mt: 0.35, fontSize: 13, color: "text.secondary" }}>
          {item?.nextDueDate ? formatRelativeAge(item?.nextDueDate) : "Belum ada due"}
        </Typography>
      </Box>

      <Box sx={{ py: 1.4, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 15,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item?.nextActionNote || "-"}
        </Typography>
      </Box>
    </Box>
  );
}

function CompletedRow({ item, onRowClick }) {
  const visual = getProcessVisualStatus(item);
  const finishedDate = item?.completedAt || item?.updatedAt;
  const durationText = formatProcessDurationText(item);

  return (
    <Box
      onClick={() => onRowClick(item)}
      sx={{
        display: "grid",
        gridTemplateColumns: "8px 2fr 0.9fr 1fr 1fr",
        gap: 2,
        alignItems: "stretch",
        borderBottom: "1px solid rgba(0,0,0,0.12)",
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "rgba(46, 125, 50, 0.05)",
        },
      }}
    >
      <Box sx={{ backgroundColor: visual.color, borderRadius: "0 4px 4px 0" }} />

      <Box sx={{ py: 1.4, minWidth: 0 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.3 }}>
          {item?.title || "-"}
        </Typography>
        <Typography
          sx={{
            mt: 0.4,
            fontSize: 14,
            color: "text.secondary",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item?.clientName || "-"} • {item?.assignedToName || "-"}
        </Typography>
      </Box>

      <Box sx={{ py: 1.4 }}>
        <Chip
          label={visual.label}
          size="small"
          sx={{
            backgroundColor: visual.bg,
            color: visual.text,
            fontWeight: 700,
          }}
        />
      </Box>

      <Box sx={{ py: 1.4 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
          {finishedDate ? formatDisplayDate(finishedDate) : "-"}
        </Typography>
        <Typography sx={{ mt: 0.35, fontSize: 13, color: "text.secondary" }}>
          {finishedDate ? formatRelativeAge(finishedDate) : "-"}
        </Typography>
      </Box>

      <Box sx={{ py: 1.4 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
          {durationText}
        </Typography>
        <Typography sx={{ mt: 0.35, fontSize: 13, color: "text.secondary" }}>
          dari mulai sampai {visual.key === "cancelled" ? "batal" : "selesai"}
        </Typography>
      </Box>
    </Box>
  );
}

function BoardSection({ title, color, rows, loading, onRowClick }) {
  return (
    <Box sx={{ mb: 7 }}>
      <Box
        sx={{
          display: "inline-block",
          px: 1.4,
          py: 0.6,
          borderRadius: 1.5,
          backgroundColor: color,
          color: "#fff",
          fontWeight: 700,
          fontSize: 18,
          mb: 1.8,
        }}
      >
        {title}
      </Box>

      <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "8px 2fr 0.95fr 1.1fr 0.9fr 0.8fr",
            gap: 2,
            px: 0,
            py: 1.2,
            backgroundColor: "rgba(0,0,0,0.03)",
            color: "text.secondary",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          <Box />
          <Box sx={{ px: 2 }}>Judul</Box>
          <Box>Status</Box>
          <Box>Stage Saat Ini</Box>
          <Box>Due Date</Box>
          <Box>Next To Do</Box>
        </Box>

        {loading ? (
          <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography color="text.secondary">Tidak ada data.</Typography>
          </Box>
        ) : (
          rows.map((item) => (
            <BoardRow key={item?._id || `${item?.title}-${item?.startDate}`} item={item} onRowClick={onRowClick} />
          ))
        )}
      </Paper>
    </Box>
  );
}

function CompletedSection({ title, color, rows, loading, onRowClick }) {
  return (
    <Box sx={{ mb: 7 }}>
      <Box
        sx={{
          display: "inline-block",
          px: 1.4,
          py: 0.6,
          borderRadius: 1.5,
          backgroundColor: color,
          color: "#fff",
          fontWeight: 700,
          fontSize: 18,
          mb: 1.8,
        }}
      >
        {title}
      </Box>

      <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "8px 2fr 0.9fr 1fr 1fr",
            gap: 2,
            px: 0,
            py: 1.2,
            backgroundColor: "rgba(0,0,0,0.03)",
            color: "text.secondary",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          <Box />
          <Box sx={{ px: 2 }}>Judul</Box>
          <Box>Status</Box>
          <Box>Tanggal Finish/Cancel</Box>
          <Box>Durasi</Box>
        </Box>

        {loading ? (
          <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography color="text.secondary">Tidak ada data.</Typography>
          </Box>
        ) : (
          rows.map((item) => (
            <CompletedRow key={item?._id || `${item?.title}-${item?.completedAt}`} item={item} onRowClick={onRowClick} />
          ))
        )}
      </Paper>
    </Box>
  );
}

function LegalProcessDialog({
  open,
  mode,
  processItem,
  templates,
  employees,
  actorName,
  loading,
  onClose,
  onSave,
}) {
  const [form, setForm] = useState(() =>
    buildInitialForm({ processItem, templates, actorName })
  );
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [inquiryKeyword, setInquiryKeyword] = useState("");
  const [inquiryResults, setInquiryResults] = useState([]);
  const [searchingInquiry, setSearchingInquiry] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [titleTouched, setTitleTouched] = useState(false);

  const assigneeOptions = useMemo(
    () => uniqueEmployeeNames(employees, actorName),
    [employees, actorName]
  );

  const currentTemplate = useMemo(() => {
    const activeTemplates = (Array.isArray(templates) ? templates : []).filter(
      (item) => item?.isActive !== false
    );

    return (
      findTemplateByKey(templates, form.serviceKey) ||
      activeTemplates[0] ||
      templates[0] ||
      null
    );
  }, [templates, form.serviceKey]);

  const currentStages = useMemo(() => {
    return getTemplateStages(currentTemplate);
  }, [currentTemplate]);

  const currentStageObj = useMemo(() => {
    return findStageByKey(currentTemplate, form.currentStageKey);
  }, [currentTemplate, form.currentStageKey]);

  const historyItems = useMemo(() => {
    const items = Array.isArray(processItem?.history) ? [...processItem.history] : [];
    return items.sort((a, b) => {
      const aTime = toDateOrNull(a?.changedAt)?.getTime() || 0;
      const bTime = toDateOrNull(b?.changedAt)?.getTime() || 0;
      return bTime - aTime;
    });
  }, [processItem]);

  useEffect(() => {
    if (!open) return;

    const nextForm = buildInitialForm({
      processItem,
      templates,
      actorName,
    });

    setForm(nextForm);
    setSelectedInquiry(toInquiryFromProcess(processItem));
    setInquiryKeyword("");
    setInquiryResults([]);
    setSearchingInquiry(false);
    setSaveError("");
    setSaving(false);
    setTitleTouched(mode === "edit");
  }, [open, mode, processItem, templates, actorName]);

  useEffect(() => {
    if (!open || titleTouched) return;

    const nextTitle = buildTitle(currentTemplate?.name || "", form.entityName || "");
    setForm((prev) => {
      if (prev.title === nextTitle) return prev;
      return { ...prev, title: nextTitle };
    });
  }, [open, titleTouched, currentTemplate, form.entityName]);

  useEffect(() => {
    if (!open || mode !== "create") return;

    const keyword = normalizeText(inquiryKeyword);

    if (keyword.length < 2) {
      setInquiryResults([]);
      setSearchingInquiry(false);
      return;
    }

    let active = true;

    const timer = setTimeout(async () => {
      try {
        setSearchingInquiry(true);

        const isPhoneSearch = /^\+?\d+$/.test(keyword);
        let resp;

        try {
          resp = await axios.get(`${LEGAL_BASE_URL}/inquiries/search`, {
            params: {
              q: keyword,
              searchThis: isPhoneSearch ? "Phone" : "Nama",
              limit: 15,
            },
          });
        } catch (error) {
          logAxiosError("[LEGAL FE][Inquiry Search][Primary ERR]", error);

          if (error?.response?.status === 404) {
            resp = await axios.get(`${LEGAL_BASE_URL}/inquiries/search`, {
              params: {
                query: keyword,
                limit: 15,
              },
            });
          } else {
            throw error;
          }
        }

        if (!active) return;

        const items = extractListFromResponse(resp);
        setInquiryResults(items);
      } catch (error) {
        logAxiosError("[LEGAL FE][Inquiry Search][ERR]", error);
        if (active) setInquiryResults([]);
      } finally {
        if (active) setSearchingInquiry(false);
      }
    }, 350);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [open, mode, inquiryKeyword]);

  const handleSelectInquiry = (item) => {
    setSelectedInquiry(item);
    setSaveError("");

    setForm((prev) => ({
      ...prev,
      inquiryId: item?._id || "",
      currentPic: prev.currentPic || normalizeText(item?.Sales),
    }));
  };

  const handleServiceChange = (serviceKey) => {
    const nextTemplate = findTemplateByKey(templates, serviceKey);
    const firstStage = getTemplateStages(nextTemplate)[0] || null;

    setForm((prev) => ({
      ...prev,
      serviceKey,
      currentStageKey: normalizeText(firstStage?.key),
      currentResponsibility:
        normalizeText(firstStage?.defaultResponsibility) || "Internal Legal",
      title: titleTouched
        ? prev.title
        : buildTitle(nextTemplate?.name || "", prev.entityName || ""),
    }));
  };

  const handleStageChange = (stageKey) => {
    const nextStage = findStageByKey(currentTemplate, stageKey);

    setForm((prev) => ({
      ...prev,
      currentStageKey: stageKey,
      currentResponsibility:
        normalizeText(nextStage?.defaultResponsibility) ||
        prev.currentResponsibility ||
        "Internal Legal",
    }));
  };

  const handleDueDateChange = (value) => {
    const normalized =
      value && dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : "";

    setForm((prev) => ({
      ...prev,
      nextDueDate: normalized,
    }));
  };

  const handleSubmit = async () => {
    const finalInquiryId =
      normalizeText(selectedInquiry?._id) ||
      normalizeText(form.inquiryId) ||
      normalizeText(processItem?.inquiryId);

    if (!finalInquiryId) {
      setSaveError("Pilih client dari Inquiry dulu.");
      return;
    }

    if (!normalizeText(form.serviceKey)) {
      setSaveError("Pilih layanan / kebutuhan.");
      return;
    }

    if (!normalizeText(form.currentStageKey)) {
      setSaveError("Pilih stage saat ini.");
      return;
    }

    if (!normalizeText(form.currentResponsibility)) {
      setSaveError("Pilih tanggung jawab saat ini.");
      return;
    }

    if (!normalizeText(form.assignedToName)) {
      setSaveError("Pilih Assigned To.");
      return;
    }

    const payload = {
      inquiryId: finalInquiryId,
      entityName: normalizeText(form.entityName),
      title:
        normalizeText(form.title) ||
        buildTitle(currentTemplate?.name || "", form.entityName || ""),
      serviceKey: normalizeText(form.serviceKey),
      currentStageKey: normalizeText(form.currentStageKey),
      currentResponsibility: normalizeText(form.currentResponsibility),
      currentPic: normalizeText(form.currentPic),
      assignedToName: normalizeText(form.assignedToName),
      startDate: formatInputDate(form.startDate) || todayInputValue(),
      nextDueDate: formatInputDate(form.nextDueDate) || "",
      nextActionNote: normalizeText(form.nextActionNote),
      actorName: normalizeText(actorName) || "System",
    };

    try {
      setSaving(true);
      setSaveError("");
      await onSave(payload);
    } catch (error) {
      setSaveError(error?.message || "Gagal menyimpan legal process.");
    } finally {
      setSaving(false);
    }
  };

  const clientName = selectedInquiry?.Nama || processItem?.clientName || "-";
  const phoneNumber = selectedInquiry?.Phone || processItem?.inquiryPhone || "-";

  const visual = getProcessVisualStatus({
    ...processItem,
    currentStageKey: form.currentStageKey,
    currentStageLabel: currentStageObj?.label || processItem?.currentStageLabel,
    nextDueDate: form.nextDueDate || processItem?.nextDueDate,
  });

  const updatedAt =
    processItem?.updatedAt ||
    historyItems?.[0]?.changedAt ||
    processItem?.history?.[processItem?.history?.length - 1]?.changedAt;

  const nextActionValue = normalizeText(form.nextActionNote || processItem?.nextActionNote);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: { xs: 24, md: 34 }, fontWeight: 700 }}>
              {mode === "create" ? "Tambah Legal Process" : "Edit Legal Process"}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.4 }}>
              {mode === "create"
                ? "Cari client dari Inquiry, pilih layanan, lalu simpan."
                : "Update stage, due date, assigned to, dan next to do."}
            </Typography>
          </Box>

          <Button
            onClick={onClose}
            sx={{
              minWidth: 40,
              px: 0,
              fontSize: 28,
              lineHeight: 1,
              color: "text.secondary",
            }}
          >
            ×
          </Button>
        </Box>

        {loading ? (
          <Box
            sx={{
              minHeight: 320,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Paper
              variant="outlined"
              sx={{
                p: 2.2,
                mb: 2.5,
                borderRadius: 2.2,
                borderLeft: `8px solid ${visual.color}`,
                background: "linear-gradient(180deg, #f8fafc 0%, #eef3f8 100%)",
                boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
              }}
            >
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.1 }}>
                <Chip
                  label={visual.label}
                  sx={{
                    backgroundColor: visual.bg,
                    color: visual.text,
                    fontWeight: 700,
                  }}
                />
                <Chip
                  label={currentStageObj?.label || processItem?.currentStageLabel || "-"}
                  variant="outlined"
                  sx={{
                    fontWeight: 700,
                    backgroundColor: "#fff",
                  }}
                />
              </Box>

              <Typography sx={{ fontSize: { xs: 22, md: 30 }, fontWeight: 700 }}>
                {normalizeText(form.title) || "-"}
              </Typography>

              <Typography color="text.secondary" sx={{ mt: 0.6, mb: 1.8, fontSize: 16 }}>
                {clientName} • {phoneNumber}
              </Typography>

              <Grid container spacing={1.5}>
                <Grid item xs={12} md={4}>
                  <SummaryInfo label="Assigned To" value={form.assignedToName || "-"} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <SummaryInfo
                    label="Due Date Selanjutnya"
                    value={form.nextDueDate ? formatDisplayDateLong(form.nextDueDate) : "-"}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <SummaryInfo
                    label="Update Terakhir"
                    value={
                      updatedAt
                        ? `${formatDisplayDateLong(updatedAt)} (${formatRelativeAge(updatedAt)})`
                        : "-"
                    }
                  />
                </Grid>
              </Grid>

              <Box
                sx={{
                  mt: 1.6,
                  p: 1.5,
                  borderRadius: 1.6,
                  backgroundColor: "#fff",
                  border: "1px solid rgba(0,0,0,0.08)",
                }}
              >
                <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 0.45 }}>
                  Next To Do / Catatan Saat Ini
                </Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.55 }}>
                  {nextActionValue || "-"}
                </Typography>
              </Box>
            </Paper>

            <Grid container spacing={2.5}>
              <Grid item xs={12} md={7}>
                {mode === "create" && (
                  <Paper
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      p: 2,
                      mb: 2,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, mb: 1.2 }}>
                      Cari client dari Inquiry
                    </Typography>

                    <TextField
                      fullWidth
                      label="Cari Nama / Nomor Telpon / Email"
                      placeholder="Contoh: Afifah / 62812 / email"
                      value={inquiryKeyword}
                      onChange={(event) => setInquiryKeyword(event.target.value)}
                    />

                    <Paper
                      variant="outlined"
                      sx={{
                        mt: 1.5,
                        maxHeight: 240,
                        overflow: "auto",
                        borderRadius: 1.5,
                      }}
                    >
                      {selectedInquiry && (
                        <Box
                          sx={{
                            p: 1.5,
                            borderBottom: "1px solid rgba(0,0,0,0.08)",
                            backgroundColor: "rgba(76,143,232,0.06)",
                          }}
                        >
                          <Typography sx={{ fontWeight: 700 }}>
                            Client terpilih: {selectedInquiry?.Nama || "-"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedInquiry?.Phone || "-"} • Sales:{" "}
                            {selectedInquiry?.Sales || "-"}
                          </Typography>
                        </Box>
                      )}

                      {searchingInquiry ? (
                        <Box sx={{ px: 2, py: 2 }}>
                          <Typography color="text.secondary">
                            Mencari inquiry...
                          </Typography>
                        </Box>
                      ) : inquiryResults.length === 0 ? (
                        <Box sx={{ px: 2, py: 2 }}>
                          <Typography color="text.secondary">
                            Ketik minimal 2 karakter untuk cari inquiry.
                          </Typography>
                        </Box>
                      ) : (
                        <List disablePadding>
                          {inquiryResults.map((item) => (
                            <ListItemButton
                              key={item?._id}
                              divider
                              onClick={() => handleSelectInquiry(item)}
                            >
                              <ListItemText
                                primary={item?.Nama || "-"}
                                secondary={`${item?.Phone || "-"} • Sales: ${
                                  item?.Sales || "-"
                                }`}
                              />
                            </ListItemButton>
                          ))}
                        </List>
                      )}
                    </Paper>
                  </Paper>
                )}

                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Nama Client"
                        value={clientName}
                        disabled
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Nama Entitas / Nama Usaha"
                        placeholder="Contoh: Afifah Jaya"
                        value={form.entityName}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            entityName: event.target.value,
                          }))
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Judul"
                        value={form.title}
                        onChange={(event) => {
                          setTitleTouched(true);
                          setForm((prev) => ({
                            ...prev,
                            title: event.target.value,
                          }));
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        select
                        fullWidth
                        label="Kebutuhan / Layanan"
                        value={form.serviceKey}
                        onChange={(event) => handleServiceChange(event.target.value)}
                      >
                        {(Array.isArray(templates) ? templates : [])
                          .filter((item) => item?.isActive !== false)
                          .map((item) => (
                            <MenuItem key={item?._id || item?.key} value={item?.key}>
                              {item?.name}
                            </MenuItem>
                          ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        select
                        fullWidth
                        label="Stage Saat Ini"
                        value={form.currentStageKey}
                        onChange={(event) => handleStageChange(event.target.value)}
                      >
                        {currentStages.map((stage) => (
                          <MenuItem key={stage?.key} value={stage?.key}>
                            {getStageLabel(stage)}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        select
                        fullWidth
                        label="Tanggung Jawab Saat Ini"
                        value={form.currentResponsibility}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            currentResponsibility: event.target.value,
                          }))
                        }
                      >
                        {RESPONSIBILITY_OPTIONS.map((item) => (
                          <MenuItem key={item} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="PIC Saat Ini"
                        placeholder="Biasanya nama sales / PIC komunikasi"
                        value={form.currentPic}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            currentPic: event.target.value,
                          }))
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      {assigneeOptions.length > 0 ? (
                        <TextField
                          select
                          fullWidth
                          label="Assigned To (Legal Staff)"
                          value={form.assignedToName}
                          onChange={(event) =>
                            setForm((prev) => ({
                              ...prev,
                              assignedToName: event.target.value,
                            }))
                          }
                        >
                          {assigneeOptions.map((name) => (
                            <MenuItem key={name} value={name}>
                              {name}
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : (
                        <TextField
                          fullWidth
                          label="Assigned To (Legal Staff)"
                          value={form.assignedToName}
                          onChange={(event) =>
                            setForm((prev) => ({
                              ...prev,
                              assignedToName: event.target.value,
                            }))
                          }
                        />
                      )}
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Due Date Selanjutnya"
                        inputFormat="DD/MM/YYYY"
                        value={form.nextDueDate ? dayjs(form.nextDueDate, "YYYY-MM-DD") : null}
                        onChange={handleDueDateChange}
                        renderInput={(params) => (
                          <TextField {...params} fullWidth placeholder="Pilih tanggal" />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={8}
                        label="Next To Do / Catatan"
                        placeholder="Contoh: Tunggu konfirmasi klien, follow up notaris, cek kembali besok, dll."
                        value={form.nextActionNote}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            nextActionNote: event.target.value,
                          }))
                        }
                      />
                    </Grid>
                  </Grid>

                  {!!saveError && (
                    <Typography color="error" sx={{ mt: 1.8 }}>
                      {saveError}
                    </Typography>
                  )}

                  <Box
                    sx={{
                      mt: 2.2,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={saving}
                      sx={{
                        minWidth: 120,
                        textTransform: "none",
                        borderRadius: 1.5,
                        py: 1.1,
                      }}
                    >
                      {saving ? "Saving..." : "Save"}
                    </Button>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={5}>
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    p: 2,
                    minHeight: 540,
                    backgroundColor: "rgba(0,0,0,0.02)",
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: 22, mb: 1.5 }}>
                    Riwayat Perubahan
                  </Typography>

                  {historyItems.length === 0 ? (
                    <Typography color="text.secondary">
                      {mode === "create"
                        ? "Riwayat akan muncul setelah process pertama kali disimpan."
                        : "Belum ada riwayat."}
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
                      {historyItems.map((entry, index) => (
                        <HistoryCard
                          key={entry?._id || index}
                          entry={entry}
                          isLast={index === historyItems.length - 1}
                        />
                      ))}
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function LegalTaskTodo() {
  const reduxUser = useSelector((state) => state?.user || {});
  const nestedUser = reduxUser?.user || {};

  const actorName =
    normalizeText(reduxUser?.full_name) ||
    normalizeText(reduxUser?.sales) ||
    normalizeText(reduxUser?.name) ||
    normalizeText(reduxUser?.username) ||
    normalizeText(nestedUser?.full_name) ||
    normalizeText(nestedUser?.sales) ||
    normalizeText(nestedUser?.name) ||
    normalizeText(nestedUser?.username);

  const actorRole =
    normalizeText(reduxUser?.role) || normalizeText(nestedUser?.role);

  const username =
    normalizeText(reduxUser?.username) || normalizeText(nestedUser?.username);

  const [templates, setTemplates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [urgent, setUrgent] = useState([]);
  const [pending, setPending] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [canViewAll, setCanViewAll] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [dialogLoading, setDialogLoading] = useState(false);

  const activeTemplateCount = useMemo(() => {
    return (Array.isArray(templates) ? templates : []).filter(
      (item) => item?.isActive !== false
    ).length;
  }, [templates]);

  const loadPageData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError("");

      let nextUrgent = [];
      let nextPending = [];
      let nextCompleted = [];
      let nextCanViewAll = isOwnerLike(actorName, actorRole, username);
      let nextTemplates = [];
      let nextEmployees = [];
      const errorParts = [];

      const [boardResult, bootstrapResult] = await Promise.allSettled([
        axios.get(`${LEGAL_BASE_URL}/board`, {
          params: {
            actorName,
            actorRole,
            username,
          },
        }),
        axios.get(`${LEGAL_BASE_URL}/bootstrap`),
      ]);

      if (boardResult.status === "fulfilled") {
        const boardData = boardResult.value?.data || {};
        const nestedBoardData =
          boardData?.data && typeof boardData.data === "object" ? boardData.data : null;

        nextUrgent = Array.isArray(boardData?.urgent)
          ? boardData.urgent
          : Array.isArray(nestedBoardData?.urgent)
          ? nestedBoardData.urgent
          : [];

        nextPending = Array.isArray(boardData?.pending)
          ? boardData.pending
          : Array.isArray(nestedBoardData?.pending)
          ? nestedBoardData.pending
          : [];

        nextCompleted = Array.isArray(boardData?.completed)
          ? boardData.completed
          : Array.isArray(nestedBoardData?.completed)
          ? nestedBoardData.completed
          : [];

        if (typeof boardData?.canViewAll === "boolean") {
          nextCanViewAll = boardData.canViewAll;
        } else if (typeof nestedBoardData?.canViewAll === "boolean") {
          nextCanViewAll = nestedBoardData.canViewAll;
        }
      } else {
        logAxiosError("[LEGAL FE][loadPageData][board ERR]", boardResult.reason);

        try {
          const processResp = await axios.get(`${LEGAL_BASE_URL}/processes`, {
            params: {
              actorName,
              actorRole,
              username,
              includeCompleted: true,
            },
          });

          const items = extractListFromResponse(processResp);
          const split = splitProcessesForBoardLocal(items);

          nextUrgent = split.urgent;
          nextPending = split.pending;
          nextCompleted = split.completed;
        } catch (fallbackError) {
          logAxiosError("[LEGAL FE][loadPageData][process fallback ERR]", fallbackError);
          errorParts.push(extractErrorMessage(fallbackError, "Gagal memuat board legal."));
        }
      }

      if (bootstrapResult.status === "fulfilled") {
        nextTemplates = Array.isArray(bootstrapResult.value?.data?.templates)
          ? bootstrapResult.value.data.templates
          : Array.isArray(bootstrapResult.value?.data?.data?.templates)
          ? bootstrapResult.value.data.data.templates
          : [];

        nextEmployees = Array.isArray(bootstrapResult.value?.data?.employees)
          ? bootstrapResult.value.data.employees
          : Array.isArray(bootstrapResult.value?.data?.data?.employees)
          ? bootstrapResult.value.data.data.employees
          : [];
      } else {
        logAxiosError("[LEGAL FE][loadPageData][bootstrap ERR]", bootstrapResult.reason);

        const [templatesResult, employeesResult] = await Promise.allSettled([
          axios.get(`${LEGAL_BASE_URL}/templates`, {
            params: {
              includeInactive: false,
            },
          }),
          axios.get(`${LEGAL_BASE_URL}/employees`),
        ]);

        if (templatesResult.status === "fulfilled") {
          nextTemplates = extractListFromResponse(templatesResult.value);
        } else {
          logAxiosError("[LEGAL FE][loadPageData][templates fallback ERR]", templatesResult.reason);
          errorParts.push(
            extractErrorMessage(
              templatesResult.reason,
              "Gagal memuat template layanan legal."
            )
          );
        }

        if (employeesResult.status === "fulfilled") {
          nextEmployees = extractListFromResponse(employeesResult.value);
        } else {
          logAxiosError("[LEGAL FE][loadPageData][employees fallback ERR]", employeesResult.reason);
        }
      }

      setUrgent(Array.isArray(nextUrgent) ? nextUrgent : []);
      setPending(Array.isArray(nextPending) ? nextPending : []);
      setCompleted(Array.isArray(nextCompleted) ? nextCompleted : []);
      setCanViewAll(!!nextCanViewAll);
      setTemplates(Array.isArray(nextTemplates) ? nextTemplates : []);
      setEmployees(Array.isArray(nextEmployees) ? nextEmployees : []);

      setLoadError(errorParts.filter(Boolean).join(" | "));
    } catch (error) {
      logAxiosError("[LEGAL FE][loadPageData][FATAL ERR]", error);
      setLoadError(extractErrorMessage(error, "Gagal memuat legal dashboard."));
      setUrgent([]);
      setPending([]);
      setCompleted([]);
      setTemplates([]);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [actorName, actorRole, username]);

  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  const handleOpenCreate = () => {
    setDialogMode("create");
    setSelectedProcess(null);
    setDialogLoading(false);
    setDialogOpen(true);
  };

  const handleOpenEdit = async (row) => {
    setDialogMode("edit");
    setSelectedProcess(row);
    setDialogLoading(true);
    setDialogOpen(true);

    try {
      const resp = await axios.get(`${LEGAL_BASE_URL}/processes/${row?._id}`);
      setSelectedProcess(extractObjectFromResponse(resp) || row);
    } catch (error) {
      logAxiosError("[LEGAL FE][Open Edit][ERR]", error);
      setSelectedProcess(row);
    } finally {
      setDialogLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogMode("create");
    setSelectedProcess(null);
    setDialogLoading(false);
  };

  const handleSaveProcess = async (payload) => {
    try {
      if (dialogMode === "create") {
        await axios.post(`${LEGAL_BASE_URL}/processes`, {
          ...payload,
          actorName: normalizeText(actorName) || "System",
        });
      } else {
        await axios.put(`${LEGAL_BASE_URL}/processes/${selectedProcess?._id}`, {
          ...payload,
          actorName: normalizeText(actorName) || "System",
        });
      }

      await loadPageData();
      handleCloseDialog();
    } catch (error) {
      logAxiosError("[LEGAL FE][Save Process][ERR]", error);
      throw new Error(extractErrorMessage(error, "Gagal menyimpan legal process."));
    }
  };

  const overdueCount = urgent.length;
  const activeCount = pending.length;
  const completedCount = completed.length;
  const totalActiveCount = overdueCount + activeCount;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Grid container>
        <Grid item xl={0.5} lg={0.7} md={0.9} sm={3.2}>
          <LeftMenu />
        </Grid>

        <Grid item xl={10.8} lg={10.6} md={10.4} sm={8.8} xs={12}>
          <Box sx={{ mt: 15 }} />

          <Box sx={{ pb: 5, px: { xs: 2, md: 3 } }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
                flexWrap: "wrap",
                mb: 3,
              }}
            >
              <Box>
                <Typography sx={{ fontSize: { xs: 28, md: 38 }, fontWeight: 700 }}>
                  Legal Dashboard
                </Typography>

                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  {canViewAll
                    ? "Mode owner/CEO: melihat semua proses."
                    : `Mode user: ${actorName || "-"}`}
                </Typography>

                <Typography color="text.secondary" sx={{ mt: 0.25 }}>
                  Template aktif: {activeTemplateCount}
                </Typography>

                {!!loadError && (
                  <Typography color="error" sx={{ mt: 1 }}>
                    {loadError}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: "flex", gap: 1.2, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    window.location.href = "/#/";
                  }}
                >
                  Home
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => {
                    window.location.href = "/#/legalstagesedit";
                  }}
                >
                  Edit Stages
                </Button>

                <Button variant="outlined" onClick={loadPageData}>
                  Refresh
                </Button>

                <Button
                  variant="contained"
                  onClick={handleOpenCreate}
                  sx={{
                    px: 3.2,
                    py: 1.1,
                    borderRadius: 2,
                    fontSize: 18,
                    textTransform: "none",
                  }}
                >
                  Tambah +
                </Button>
              </Box>
            </Box>

            <Grid container spacing={1.6} sx={{ mb: 3.2 }}>
              <Grid item xs={12} md={3}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
                    Total Process Aktif
                  </Typography>
                  <Typography sx={{ fontSize: 30, fontWeight: 700, mt: 0.5 }}>
                    {totalActiveCount}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={3}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    borderLeft: "6px solid #d32f2f",
                    backgroundColor: "#fffafa",
                  }}
                >
                  <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
                    Overdue
                  </Typography>
                  <Typography sx={{ fontSize: 30, fontWeight: 700, mt: 0.5, color: "#b71c1c" }}>
                    {overdueCount}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={3}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    borderLeft: "6px solid #1565c0",
                    backgroundColor: "#fafcff",
                  }}
                >
                  <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
                    Active / Belum Overdue
                  </Typography>
                  <Typography sx={{ fontSize: 30, fontWeight: 700, mt: 0.5, color: "#0d47a1" }}>
                    {activeCount}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={3}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    borderLeft: "6px solid #2e7d32",
                    backgroundColor: "#f7fff8",
                  }}
                >
                  <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
                    Finish / Cancelled
                  </Typography>
                  <Typography sx={{ fontSize: 30, fontWeight: 700, mt: 0.5, color: "#1b5e20" }}>
                    {completedCount}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <BoardSection
              title="Overdue"
              color="#d32f2f"
              rows={urgent}
              loading={loading}
              onRowClick={handleOpenEdit}
            />

            <BoardSection
              title="Active"
              color="#1565c0"
              rows={pending}
              loading={loading}
              onRowClick={handleOpenEdit}
            />

            <CompletedSection
              title="Finish / Cancelled"
              color="#2e7d32"
              rows={completed}
              loading={loading}
              onRowClick={handleOpenEdit}
            />
          </Box>
        </Grid>

        <LegalProcessDialog
          open={dialogOpen}
          mode={dialogMode}
          processItem={selectedProcess}
          templates={templates}
          employees={employees}
          actorName={actorName}
          loading={dialogLoading}
          onClose={handleCloseDialog}
          onSave={handleSaveProcess}
        />
      </Grid>
    </LocalizationProvider>
  );
}